import React, { useMemo, Fragment } from 'react';
import classNames from 'classnames';

// Sourced and adapted from unmaintained package
// @see https://github.com/deoxxa/paginator/blob/db4044e/index.js
function calcPaginationInfo({
  perPage,
  pagesToDisplay,
  totalResults,
  currentPage,
}: {
  perPage: number;
  pagesToDisplay: number;
  totalResults: number;
  currentPage: number;
}) {
  // We want the number of pages, rounded up to the nearest page.
  const totalPages = Math.ceil(totalResults / perPage);

  // This is the first page to be displayed as a numbered link.
  var firstPage = Math.max(1, currentPage - Math.floor(pagesToDisplay / 2));

  // And here's the last page to be displayed specifically.
  var lastPage = Math.min(totalPages, currentPage + Math.floor(pagesToDisplay / 2));

  // This is triggered if we're at or near one of the extremes; we won't have
  // enough page links. We need to adjust our bounds accordingly.
  if (lastPage - firstPage + 1 < pagesToDisplay) {
    if (currentPage < totalPages / 2) {
      lastPage = Math.min(totalPages, lastPage + (pagesToDisplay - (lastPage - firstPage)));
    } else {
      firstPage = Math.max(1, firstPage - (pagesToDisplay - (lastPage - firstPage)));
    }
  }

  // This can be triggered if the user wants an odd number of pages.
  if (lastPage - firstPage + 1 > pagesToDisplay) {
    // We want to move towards whatever extreme we're closest to at the time.
    if (currentPage > totalPages / 2) {
      firstPage++;
    } else {
      lastPage--;
    }
  }

  // First result on the page. This, along with the field below, can be used to
  // do "showing x to y of z results" style things.
  let firstResult = perPage * (currentPage - 1);
  if (firstResult < 0) {
    firstResult = 0;
  }

  // Last result on the page.
  let lastResult = perPage * currentPage - 1;
  if (lastResult < 0) {
    lastResult = 0;
  }
  if (lastResult > Math.max(totalResults - 1, 0)) {
    lastResult = Math.max(totalResults - 1, 0);
  }

  return {
    totalPages: totalPages,
    pages: Math.min(lastPage - firstPage + 1, totalPages),
    currentPage,
    firstPage,
    lastPage,
    previousPage: currentPage - 1,
    nextPage: currentPage + 1,
    hasPreviousPage: currentPage > 1,
    hasNextPage: currentPage < totalPages,
    totalResults,
    results: Math.min(lastResult - firstResult + 1, totalResults),
    firstResult,
    lastResult,
  };
}

const PageBlock: React.FC<{
  label: string | number;
  ariaLabel?: string;
  onClick: () => void;
  linkClassName?: string | false;
  itemClassName?: string | false;
}> = ({ label, ariaLabel, onClick, linkClassName, itemClassName }) => {
  return (
    <li
      className={classNames(
        'text-gray-700 cursor-pointer px-3 py-2 bg-white rounded mx-1 border border-gray-200 outline-none flex items-center justify-center',
        itemClassName,
      )}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={classNames('font-medium outline-none', linkClassName)}
        href="#"
        aria-label={ariaLabel}
      >
        {label}
      </a>
    </li>
  );
};

interface Props {
  totalItemsCount: number;
  activePage: number;
  onChange: (pageNumber: number) => void;
  pageRangeDisplayed: number;
  itemsCountPerPage: number;
}

// Sourced and adapted from unmaintained package
// @see https://github.com/wwwaiser/react-js-pagination/blob/1c19fa1/src/components/Pagination.js
export const Pagination: React.FC<Props> = ({
  totalItemsCount,
  activePage,
  onChange,
  pageRangeDisplayed,
  itemsCountPerPage,
}) => {
  const pages = useMemo(() => {
    const pgs = [] as JSX.Element[];

    const paginationInfo = calcPaginationInfo({
      perPage: itemsCountPerPage,
      pagesToDisplay: pageRangeDisplayed,
      totalResults: totalItemsCount,
      currentPage: activePage,
    });

    if (paginationInfo.hasPreviousPage) {
      pgs.push(
        <Fragment key="first-previous">
          <PageBlock label="First" ariaLabel="Go to first page" onClick={() => onChange(1)} />
          <PageBlock
            label="⟨"
            ariaLabel="Go to previous page"
            onClick={() => onChange(paginationInfo.previousPage)}
          />
        </Fragment>,
      );
    }

    for (let i = paginationInfo.firstPage; i <= paginationInfo.lastPage; i++) {
      const isActivePage = i === activePage;

      pgs.push(
        <PageBlock
          key={i}
          label={i}
          ariaLabel={`Go to page number ${i}`}
          onClick={() => onChange(i)}
          itemClassName={isActivePage && 'bg-gray-700'}
          linkClassName={isActivePage && 'text-white'}
        />,
      );
    }

    if (paginationInfo.hasNextPage) {
      pgs.push(
        <Fragment key="next-last">
          <PageBlock
            label="⟩"
            ariaLabel="Go to next page"
            onClick={() => onChange(paginationInfo.nextPage)}
          />
          <PageBlock
            label="Last"
            ariaLabel="Go to last page"
            onClick={() => onChange(paginationInfo.totalPages)}
          />
        </Fragment>,
      );
    }

    return pgs;
  }, [totalItemsCount, pageRangeDisplayed, activePage, onChange]);

  return <ul className="flex flex-row">{pages}</ul>;
};
